import { type FC, type HTMLAttributes } from 'react';

import { FacetedFilterOperator } from '@shared/api/findings/findings.types';
import { type FacetedFilter } from '@shared/api/findings/findings.types';
import { Separator } from '@shared/components/ui';
import { cn } from '@shared/utils';
import { getFiltersZeroCount } from '@shared/utils/findings/trace-findings.helpers';
import { normalizeFiltersForUI } from '@shared/utils/findings/trace-findings.utils';

import { FilterGrid } from './FilterGrid';

interface FiltersListSectionProps extends HTMLAttributes<HTMLDivElement> {
  filtersData: FacetedFilter[];
  filterSearch: string;
  selectedFields: string[];
  toggleField: (field: string) => void;
}

export const FiltersListSection: FC<FiltersListSectionProps> = ({
  filtersData,
  filterSearch,
  selectedFields,
  toggleField,
  className,
}) => {
  const mappedFilters = normalizeFiltersForUI(filtersData);

  const filtersZeroCount = getFiltersZeroCount(mappedFilters, filterSearch);
  const search = filterSearch.trim().toLowerCase();
  const filtersWithCount: FacetedFilter[] = [];

  const filteredFilters: FacetedFilter[] = filtersData.map((f) => {
    let operator: FacetedFilterOperator;
    if (f.operator === FacetedFilterOperator.IS) operator = FacetedFilterOperator.IS;
    else if (f.operator === FacetedFilterOperator.NOT) operator = FacetedFilterOperator.NOT;
    else operator = FacetedFilterOperator.IS;
    return {
      ...f,
      key: f.key,
      operator,
      options: f.options.filter((opt) => opt.value !== ''),
    };
  });

  for (const filter of filteredFilters) {
    const totalCount = filter.options.reduce((acc, opt) => acc + (opt.count ?? 0), 0);
    if (search && !filter.label.toLowerCase().includes(search)) continue;
    if (totalCount > 0) {
      filtersWithCount.push(filter);
    }
  }

  return (
    <div className={cn('max-h-52 overflow-y-auto scroll-container', className)} data-testid="filters-list-section-root">
      {filtersWithCount.length > 0 && (
        <div>
          <FilterGrid
            filters={filtersWithCount}
            selectedFields={selectedFields}
            toggleField={toggleField}
            forceZeroCount={false}
          />
        </div>
      )}
      {filtersZeroCount.length > 0 && (
        <>
          <Separator className="my-4 bg-neutral-700/20" data-testid="filters-list-section-separator" />
          <div className="opacity-60">
            <FilterGrid
              filters={filtersZeroCount}
              selectedFields={selectedFields}
              toggleField={toggleField}
              forceZeroCount={true}
            />
          </div>
        </>
      )}
    </div>
  );
};
